import React, { useState, useMemo } from 'react';
import { Card, Collapse, IconButton, TableCell, TableRow } from '@mui/material';
import ArgonBox from 'components/ArgonBox';
import ArgonTypography from 'components/ArgonTypography';
import Icon from '@mui/material/Icon';
import { grey } from '@mui/material/colors';
import PropTypes from 'prop-types';
import StageExpandableTable from '../tabels/StageExpandableTable';

// Import the reusable DiffRenderer component
import DiffRenderer from './DiffRenderer';

function RevenueAttributes({ selectedOrg, productData, showSameFields }) {
  const [expandedRow, setExpandedRow] = useState(null);

  const columns = [
    { name: 'attribute', align: 'left' },
    { name: 'actions', align: 'right', width: '50px' },
  ];

  const handleRowClick = (rowId) => {
    setExpandedRow((prevExpandedRow) => (prevExpandedRow === rowId ? null : rowId));
  };

  // useMemo hook to efficiently process the product data and generate rows.
  // This calculation only re-runs when the productData prop changes.
  const { groups, hasAnyAttributes } = useMemo(() => {
    const generatedGroups = { new: [], changed: [], deleted: [], same: [] };
    let foundAnyAttributes = false;

    if (!productData) {
      return { groups: generatedGroups, hasAnyAttributes: false };
    }

    productData.forEach((productDiff) => {
      const productName = productDiff.product.name || 'Unknown Product';

      // Use default empty object {} to prevent crashes if properties are missing
      const overrides = productDiff.attributeOverrides || {};
      const classifications = productDiff.classificationAttributes || {};

      const hasOverrides = Object.values(overrides).some((arr) => arr.length > 0);

      const attributesToProcess = hasOverrides ? overrides : classifications;
      const sObjectName = hasOverrides ? 'AttributeOverride__c' : 'ClassificationAttribute__c';

      // Check if there's anything to process
      if (Object.values(attributesToProcess).some((arr) => arr.length > 0)) {
        foundAnyAttributes = true;
        ['new', 'changed', 'deleted', 'same'].forEach((type) => {
          (attributesToProcess[type] || []).forEach((attribute) => {
            generatedGroups[type].push({
              id: attribute.recordId || attribute.id,
              attribute: (
                <ArgonTypography variant="caption" color="secondary" fontWeight="bold">
                  {`${productName} - ${attribute.name || attribute.fields?.Name || 'Attribute'}`}
                </ArgonTypography>
              ),
              sObjectName: sObjectName,
              changes: attribute.changes,
            });
          });
        });
      }
    });

    return { groups: generatedGroups, hasAnyAttributes: foundAnyAttributes };
  }, [productData]);

  // This function now simply renders the reusable DiffRenderer inside an expandable row.
  const renderExpandedContent = (row) => {
    return (
      <TableRow>
        <TableCell colSpan={columns.length} style={{ padding: '0px', borderBottom: 'none' }}>
          <Collapse in={expandedRow === row.id} timeout="auto" unmountOnExit>
            <ArgonBox p={3} color="text" bgcolor={grey[50]}>
              <DiffRenderer
                changes={row.changes || []}
                sObjectName={row.sObjectName}
                orgType={selectedOrg.type.toLowerCase()}
                showSameFields={showSameFields}
              />
            </ArgonBox>
          </Collapse>
        </TableCell>
      </TableRow>
    );
  };

  // Helper to format rows for the expandable table component.
  const formattedRows = (rows) => {
    return rows.map((row) => ({
      ...row,
      renderExpandedContent: () => renderExpandedContent(row),
      onClick: () => handleRowClick(row.id),
      actions: (
        <IconButton
          aria-label="expand row"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            handleRowClick(row.id);
          }}
        >
          <Icon>{expandedRow === row.id ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</Icon>
        </IconButton>
      ),
    }));
  };

  // Helper to render a section (e.g., "New", "Changed") if it has data.
  const renderTableSection = (title, rows) => {
    if (!rows || rows.length === 0) {
      return null;
    }
    return (
      <ArgonBox mb={3}>
        <Card>
          <ArgonBox p={3}>
            <ArgonTypography variant="h6" fontWeight="medium" mb={2}>
              {title}
            </ArgonTypography>
            <ArgonBox
              sx={{
                '& .MuiTableRow-root:not(:last-child)': {
                  '& td': {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
              }}
            >
              <StageExpandableTable
                rows={formattedRows(rows)}
                columns={columns}
                renderRowSubComponent={({ row }) => row.renderExpandedContent()}
              />
            </ArgonBox>
          </ArgonBox>
        </Card>
      </ArgonBox>
    );
  };

  // Main render logic for the component.
  // If no attributes were found at all, display the message.
  if (!hasAnyAttributes) {
    return (
      <Card>
        <ArgonBox p={3} textAlign="center">
          <ArgonTypography variant="body2" color="textSecondary">
            No attributes or overrides found for the selected products.
          </ArgonTypography>
        </ArgonBox>
      </Card>
    );
  }

  // Otherwise, render the tables for each change type.
  return (
    <ArgonBox>
      {renderTableSection('New Attributes', groups.new)}
      {renderTableSection('Changed Attributes', groups.changed)}
      {renderTableSection('Same Attributes', groups.same)}
      {renderTableSection('Deleted Attributes', groups.deleted)}
    </ArgonBox>
  );
}

// PropTypes remain the same.
RevenueAttributes.propTypes = {
  selectedOrg: PropTypes.object.isRequired,
  productData: PropTypes.arrayOf(
    PropTypes.shape({
      product: PropTypes.shape({
        name: PropTypes.string,
      }).isRequired,
      classificationAttributes: PropTypes.object,
      attributeOverrides: PropTypes.object,
    })
  ).isRequired,
  showSameFields: PropTypes.bool,
};

export default RevenueAttributes;
